import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus,
  Search,
  FileText,
  Upload,
  Download,
  Share2,
  Eye,
  Trash2,
  X,
  Sparkles,
  Clock,
  CheckCircle,
  FolderOpen,
  File,
  ExternalLink,
  FileCode
} from 'lucide-react';
import { toast } from 'sonner';

const docTypes = [
  { id: 'sow', label: 'Scope of Work', color: 'bg-blue-500/10 text-blue-600' },
  { id: 'sov', label: 'Schedule of Values', color: 'bg-green-500/10 text-green-600' },
  { id: 'bid', label: 'Bid/Estimate', color: 'bg-yellow-500/10 text-yellow-600' },
  { id: 'contract', label: 'Contract', color: 'bg-purple-500/10 text-purple-600' },
  { id: 'mou', label: 'MOU', color: 'bg-indigo-500/10 text-indigo-600' },
  { id: 'change_order', label: 'Change Order', color: 'bg-orange-500/10 text-orange-600' },
  { id: 'coi', label: 'Certificate of Insurance', color: 'bg-red-500/10 text-red-600' },
  { id: 'other', label: 'Other', color: 'bg-gray-500/10 text-gray-600' }
];

export const Documents = () => {
  const { api } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterJob, setFilterJob] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, templatesRes] = await Promise.all([
        api.get('/jobs'),
        api.get('/portal/templates')
      ]);
      setJobs(jobsRes.data);
      setTemplates(templatesRes.data);
      
      // Fetch documents for all jobs
      const allDocs = [];
      for (const job of jobsRes.data) {
        try {
          const docsRes = await api.get(`/documents/${job.id}`);
          allDocs.push(...docsRes.data.map(d => ({ ...d, job_name: job.client_name })));
        } catch (e) {
          // No docs for this job
        }
      }
      setDocuments(allDocs);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          doc.job_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !filterType || doc.type === filterType;
    const matchesJob = !filterJob || doc.job_id === filterJob;
    return matchesSearch && matchesType && matchesJob;
  });

  const generateDocument = async (jobId, docType, additionalNotes) => {
    setGenerating(true);
    try {
      const formData = new FormData();
      formData.append('job_id', jobId);
      formData.append('doc_type', docType);
      if (additionalNotes) formData.append('additional_notes', additionalNotes);
      
      const response = await api.post('/documents/generate', formData);
      const job = jobs.find(j => j.id === jobId);
      setDocuments([...documents, { ...response.data, job_name: job?.client_name }]);
      toast.success(`${docType.toUpperCase()} generated successfully`);
      setShowGenerateModal(false);
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error('Failed to generate document');
    } finally {
      setGenerating(false);
    }
  };

  const toggleShare = async (docId) => {
    try {
      const response = await api.patch(`/documents/${docId}/share`);
      setDocuments(documents.map(d => 
        d.id === docId ? { ...d, shared_with_client: response.data.shared_with_client } : d
      ));
      toast.success(response.data.shared_with_client ? 'Document shared with client' : 'Sharing disabled');
    } catch (error) {
      toast.error('Failed to update sharing');
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/documents/${docId}`);
      setDocuments(documents.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getDocTypeConfig = (type) => docTypes.find(t => t.id === type) || docTypes[docTypes.length - 1];

  const createFromTemplate = async (templateId, jobId, variables = {}) => {
    setGenerating(true);
    try {
      const formData = new FormData();
      formData.append('template_id', templateId);
      formData.append('job_id', jobId);
      formData.append('variables', JSON.stringify(variables));
      
      const response = await api.post('/documents/from-template', formData);
      const job = jobs.find(j => j.id === jobId);
      setDocuments([...documents, { ...response.data, job_name: job?.client_name }]);
      toast.success('Document created from template');
      setShowTemplateModal(false);
    } catch (error) {
      console.error('Error creating from template:', error);
      toast.error('Failed to create document');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl">Documents</h1>
          <p className="text-gray-500 mt-1">{documents.length} documents</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowTemplateModal(true)}
            className="inline-flex items-center gap-2 border border-[#C6A87C] text-[#C6A87C] px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#C6A87C]/5 transition-colors"
            data-testid="template-doc-btn"
          >
            <FileCode className="w-4 h-4" />
            From Template
          </button>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#B09265] transition-colors"
            data-testid="generate-doc-btn"
          >
            <Sparkles className="w-4 h-4" />
            AI Generate
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-100 p-4">
          <p className="text-xs font-semibold uppercase text-gray-400 mb-1">Total Documents</p>
          <p className="font-heading text-2xl">{documents.length}</p>
        </div>
        <div className="bg-[#C6A87C]/10 border border-[#C6A87C]/20 p-4">
          <p className="text-xs font-semibold uppercase text-[#C6A87C] mb-1">AI Generated</p>
          <p className="font-heading text-2xl text-[#B09265]">{documents.filter(d => d.ai_generated).length}</p>
        </div>
        <div className="bg-green-50 border border-green-100 p-4">
          <p className="text-xs font-semibold uppercase text-green-600 mb-1">Shared</p>
          <p className="font-heading text-2xl text-green-700">{documents.filter(d => d.shared_with_client).length}</p>
        </div>
        <div className="bg-blue-50 border border-blue-100 p-4">
          <p className="text-xs font-semibold uppercase text-blue-600 mb-1">Document Types</p>
          <p className="font-heading text-2xl text-blue-700">{new Set(documents.map(d => d.type)).size}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
            data-testid="doc-search"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white min-w-[150px]"
        >
          <option value="">All Types</option>
          {docTypes.map(type => (
            <option key={type.id} value={type.id}>{type.label}</option>
          ))}
        </select>
        <select
          value={filterJob}
          onChange={(e) => setFilterJob(e.target.value)}
          className="px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white min-w-[180px]"
        >
          <option value="">All Jobs</option>
          {jobs.map(job => (
            <option key={job.id} value={job.id}>{job.client_name}</option>
          ))}
        </select>
      </div>

      {/* Documents List */}
      {filteredDocuments.length > 0 ? (
        <div className="bg-white border border-gray-100">
          <div className="divide-y divide-gray-100">
            {filteredDocuments.map(doc => {
              const typeConfig = getDocTypeConfig(doc.type);
              return (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-gray-500" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium truncate">{doc.name}</h3>
                        {doc.ai_generated && (
                          <span className="flex items-center gap-1 text-xs text-[#C6A87C]">
                            <Sparkles className="w-3 h-3" /> AI
                          </span>
                        )}
                        {doc.shared_with_client && (
                          <span className="flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle className="w-3 h-3" /> Shared
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                        <span className={`px-2 py-0.5 text-xs ${typeConfig.color}`}>{typeConfig.label}</span>
                        <span>{doc.job_name}</span>
                        <span>v{doc.version}</span>
                        <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => { setSelectedDoc(doc); setShowDocModal(true); }}
                      className="p-2 hover:bg-gray-100 text-gray-500 hover:text-[#C6A87C]"
                      title="View"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => toggleShare(doc.id)}
                      className={`p-2 hover:bg-gray-100 ${doc.shared_with_client ? 'text-green-500' : 'text-gray-500'} hover:text-green-600`}
                      title="Share with client"
                    >
                      <Share2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => deleteDocument(doc.id)}
                      className="p-2 hover:bg-red-50 text-gray-500 hover:text-red-500"
                      title="Delete document"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-16 bg-gray-50">
          <FolderOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-400 mb-4">No documents found</p>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="text-[#C6A87C] font-semibold hover:underline"
          >
            Generate your first document
          </button>
        </div>
      )}

      {/* Generate Document Modal */}
      <AnimatePresence>
        {showGenerateModal && (
          <GenerateModal
            jobs={jobs}
            generating={generating}
            onClose={() => setShowGenerateModal(false)}
            onGenerate={generateDocument}
          />
        )}
      </AnimatePresence>

      {/* View Document Modal */}
      <AnimatePresence>
        {showDocModal && selectedDoc && (
          <ViewDocModal
            doc={selectedDoc}
            onClose={() => { setShowDocModal(false); setSelectedDoc(null); }}
          />
        )}
      </AnimatePresence>

      {/* Template Modal */}
      <AnimatePresence>
        {showTemplateModal && (
          <TemplateModal
            templates={templates}
            jobs={jobs}
            generating={generating}
            onClose={() => setShowTemplateModal(false)}
            onCreate={createFromTemplate}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Generate Document Modal
const GenerateModal = ({ jobs, generating, onClose, onGenerate }) => {
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedType, setSelectedType] = useState('sow');
  const [additionalNotes, setAdditionalNotes] = useState('');

  const activeJobs = jobs.filter(j => ['active', 'pursuing', 'bid_submitted'].includes(j.status));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-lg"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-[#C6A87C]" />
            <h2 className="font-heading text-xl">Generate AI Document</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onGenerate(selectedJob, selectedType, additionalNotes); }} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Select Job *</label>
            <select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
              data-testid="gen-job-select"
            >
              <option value="">Choose a job...</option>
              {activeJobs.map(job => (
                <option key={job.id} value={job.id}>{job.client_name} - {job.project_type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Document Type *</label>
            <div className="grid grid-cols-2 gap-2">
              {docTypes.slice(0, 6).map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setSelectedType(type.id)}
                  className={`p-3 text-sm text-left border transition-colors ${
                    selectedType === type.id
                      ? 'border-[#C6A87C] bg-[#C6A87C]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Additional Notes (Optional)</label>
            <textarea
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none resize-none"
              placeholder="Include any specific details or requirements..."
            />
          </div>

          {generating && (
            <div className="flex items-center gap-3 p-4 bg-[#C6A87C]/10 border border-[#C6A87C]/20">
              <div className="w-5 h-5 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-[#C6A87C]">Generating document with AI... This may take a moment.</span>
            </div>
          )}

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-200 text-gray-600 text-xs font-semibold uppercase"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedJob || generating}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#C6A87C] text-white text-xs font-semibold uppercase hover:bg-[#B09265] disabled:opacity-50"
              data-testid="confirm-generate-btn"
            >
              <Sparkles className="w-4 h-4" />
              {generating ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// View Document Modal
const ViewDocModal = ({ doc, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="font-heading text-xl">{doc.name}</h2>
            <p className="text-sm text-gray-500 mt-1">
              Created {new Date(doc.created_at).toLocaleDateString()} by {doc.created_by}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {doc.content ? (
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-gray-700 bg-gray-50 p-6 border border-gray-200">
                {doc.content}
              </pre>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No content available</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-4 p-6 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-3 border border-gray-200 text-gray-600 text-xs font-semibold uppercase"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};


// Template Modal
const TemplateModal = ({ templates, jobs, generating, onClose, onCreate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedJob, setSelectedJob] = useState('');

  const activeJobs = jobs.filter(j => ['active', 'pursuing', 'bid_submitted'].includes(j.status));

  const templateDescriptions = {
    contract: 'Standard construction contract with payment terms and conditions',
    change_order: 'Document changes to the original scope with cost adjustments',
    sow: 'Detailed breakdown of all work to be performed',
    sov: 'Itemized list of project costs for billing purposes',
    coi_request: 'Request letter for certificate of insurance'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-lg"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <FileCode className="w-5 h-5 text-[#C6A87C]" />
            <h2 className="font-heading text-xl">Create from Template</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Select Template *</label>
            <div className="grid grid-cols-1 gap-2">
              {templates.map(template => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`p-4 text-left border transition-colors ${
                    selectedTemplate === template.id
                      ? 'border-[#C6A87C] bg-[#C6A87C]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-medium">{template.name}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {templateDescriptions[template.id] || `${template.type} document template`}
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Select Job *</label>
            <select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
              data-testid="template-job-select"
            >
              <option value="">Choose a job...</option>
              {activeJobs.map(job => (
                <option key={job.id} value={job.id}>{job.client_name} - {job.project_type}</option>
              ))}
            </select>
          </div>

          <p className="text-sm text-gray-500 bg-gray-50 p-3">
            The template will be automatically populated with job details. You can edit the document after creation.
          </p>

          {generating && (
            <div className="flex items-center gap-3 p-4 bg-[#C6A87C]/10 border border-[#C6A87C]/20">
              <div className="w-5 h-5 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-[#C6A87C]">Creating document...</span>
            </div>
          )}

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-200 text-gray-600 text-xs font-semibold uppercase"
            >
              Cancel
            </button>
            <button
              onClick={() => onCreate(selectedTemplate, selectedJob)}
              disabled={!selectedTemplate || !selectedJob || generating}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#C6A87C] text-white text-xs font-semibold uppercase hover:bg-[#B09265] disabled:opacity-50"
              data-testid="create-from-template-btn"
            >
              <FileCode className="w-4 h-4" />
              Create Document
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
